import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getUserEmail } from "../../../utils/auth";
import BASE_URL from "../../../config/apiConfig";
import "./CropClinicSubscriptionList.css";

const CropClinicSubscriptionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSubscription, setOpenSubscription] = useState(null);

  const navigate = useNavigate();

  const isExpired = (fields = []) => {
    if (!fields.length) return false;

    const now = new Date();

    return fields.every((field) => {
      if (!field?.expires_at) return false;
      return new Date(field.expires_at) < now;
    });
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const email = getUserEmail();

        const response = await axios.post(
          `${BASE_URL}/api/payment/getTransactions`,
          { email },
        );

        setTransactions(response.data.transactions || []);
      } catch (error) {
        setError("Failed to fetch subscriptions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const toggleSubscription = (id) => {
    setOpenSubscription((current) => (current === id ? null : id));
  };

  return (
    <div className="crop-subscription-page">
      <section className="crop-subscription-hero">
        <div className="crop-subscription-hero-grid" />

        <div className="crop-subscription-hero-content">
          <span className="crop-subscription-eyebrow">FARMOLOGY PLATFORM</span>

          <h1>Crop Clinic</h1>

          <p>
            Get crop-specific support, use your available clinic hits, and
            review your previous crop consultations in one place.
          </p>
        </div>

        <div className="crop-subscription-hero-meta">
          <span>CROP CLINIC</span>

          <span>
            {transactions.length} SUBSCRIPTION
            {transactions.length === 1 ? "" : "S"}
          </span>
        </div>
      </section>

      <main className="crop-subscription-main">
        <div className="crop-subscription-heading-row">
          <div>
            <span className="crop-subscription-section-label">
              YOUR CROP SERVICES
            </span>

            <h2>Crop Clinic subscriptions</h2>

            <p>
              View your available clinic units, subscription expiry and clinic
              history.
            </p>
          </div>

          <button
            className="crop-add-farm-cta"
            onClick={() => navigate("/user/add-farm-now")}
          >
            <span>+</span>
            Add Farm
          </button>
        </div>

        {loading ? (
          <section className="crop-state-card">
            <span className="crop-spinner" />
            <strong>Loading Crop Clinic</strong>
            <span>Fetching your active crop services.</span>
          </section>
        ) : error ? (
          <section className="crop-state-card crop-state-error">
            <strong>Unable to load subscriptions</strong>
            <span>{error}</span>
          </section>
        ) : transactions.length === 0 ? (
          <section className="crop-state-card crop-empty-state">
            <div className="crop-empty-icon">+</div>

            <strong>No Crop Clinic subscriptions</strong>

            <span>
              No farms are available right now. Add your farm to get started.
            </span>

            <button
              className="crop-add-farm-cta"
              onClick={() => navigate("/user/add-farm-now")}
            >
              Add New Farm
            </button>
          </section>
        ) : (
          <section className="crop-subscriptions-list">
            {transactions.map((transaction) => {
              const expired = isExpired(transaction.fields || []);
              const clinicHits = Number(transaction.crop_clinic) || 0;
              const isOpen = openSubscription === transaction.id;
              const fields = transaction.fields || [];

              return (
                <article
                  className={`crop-subscription-card ${
                    isOpen ? "crop-subscription-card-open" : ""
                  } ${expired ? "crop-subscription-expired" : ""}`}
                  key={transaction.id}
                >
                  {/* =====================================================
                      COMPACT HEADER — ALWAYS VISIBLE
                      ===================================================== */}
                  <button
                    type="button"
                    className="crop-card-summary"
                    onClick={() => toggleSubscription(transaction.id)}
                    aria-expanded={isOpen}
                    aria-controls={`crop-subscription-details-${transaction.id}`}
                  >
                    <div className="crop-summary-crop">
                      <span className="crop-card-kicker">CROP SERVICE</span>

                      <h3>{transaction.crop_name}</h3>
                    </div>

                    <div className="crop-summary-farm">
                      <span>FARM</span>

                      <strong>
                        {fields.length
                          ? fields.map((field) => field.field_name).join(", ")
                          : "N/A"}
                      </strong>
                    </div>

                    <div className="crop-summary-units">
                      <span>AVAILABLE UNITS</span>
                      <strong>{clinicHits}</strong>
                    </div>

                    <div className="crop-summary-arrow">
                      <span
                        className={`crop-card-arrow ${isOpen ? "is-open" : ""}`}
                      >
                        ⌄
                      </span>
                    </div>
                  </button>

                  {/* =====================================================
                      EXPANDED CONTENT
                      ===================================================== */}
                  <div
                    id={`crop-subscription-details-${transaction.id}`}
                    className={`crop-card-expand ${isOpen ? "is-open" : ""}`}
                  >
                    <div className="crop-card-expand-inner">
                      <div className="crop-expanded-top">
                        <div>
                          <span className="crop-expanded-label">
                            SUBSCRIPTION DETAILS
                          </span>

                          <p>
                            Complete details for your{" "}
                            <strong>{transaction.crop_name}</strong> Crop Clinic
                            subscription.
                          </p>
                        </div>

                        <span
                          className={`crop-card-status ${
                            expired ? "expired" : "active"
                          }`}
                        >
                          {expired ? "Expired" : "Available"}
                        </span>
                      </div>

                      <div className="crop-expanded-farms">
                        <div className="crop-expanded-section-title">
                          Your farms
                        </div>

                        <div className="crop-expanded-farm-list">
                          {fields.length ? (
                            fields.map((field, index) => (
                              <div
                                className="crop-expanded-farm"
                                key={
                                  field.id ||
                                  field.field_id ||
                                  `${transaction.id}-${index}`
                                }
                              >
                                <span>FARM {index + 1}</span>

                                <strong>
                                  {field.field_name || "Unnamed farm"}
                                </strong>
                              </div>
                            ))
                          ) : (
                            <div className="crop-expanded-farm">
                              <span>FARM</span>
                              <strong>N/A</strong>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="crop-expanded-stats">
                        <div className="crop-stat">
                          <span>REMAINING UNITS</span>
                          <strong>{clinicHits}</strong>
                          <small>Clinic hits available</small>
                        </div>

                        <div className="crop-stat">
                          <span>EXPIRY</span>

                          <strong>
                            {fields.length
                              ? formatDate(fields[0].expires_at)
                              : "N/A"}
                          </strong>

                          <small>
                            {fields.length > 1
                              ? `${fields.length} farm subscriptions`
                              : "Subscription period"}
                          </small>
                        </div>
                      </div>

                      <div className="crop-button-container">
                        <button
                          className={`crop-add-farm-button ${
                            clinicHits <= 0 || expired ? "expired" : ""
                          }`}
                          disabled={clinicHits <= 0 || expired}
                          onClick={(event) => {
                            event.stopPropagation();

                            navigate(
                              `/user/crop-clinic/${transaction.crop_name}`,
                              {
                                state: {
                                  id: transaction.id,
                                  cropname: transaction.crop_name,
                                  crop_clinic: transaction.crop_clinic,
                                },
                              },
                            );
                          }}
                        >
                          {expired
                            ? "Expired"
                            : clinicHits > 0
                              ? "Open Crop Clinic"
                              : "No Trials Left"}
                        </button>

                        <button
                          className="crop-history-button"
                          onClick={(event) => {
                            event.stopPropagation();

                            navigate(
                              `/user/crop-clinic/history/${transaction.id}`,
                              {
                                state: {
                                  id: transaction.id,
                                  cropname: transaction.crop_name,
                                  crop_clinic: transaction.crop_clinic,
                                },
                              },
                            );
                          }}
                        >
                          See History
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
};

export default CropClinicSubscriptionList;
